import Vue from 'vue'
const state = {
  currentShowSeries: {},
  // Slugs for which we have loaded data (so we don't re-fetch on tab change)
  standardLoadedSlug: null,
  adminLoadedSlug: null
}

const mutations = {
  setCurrentShowSeries: (state, payload) => {
    if (payload.series) {
      state.currentShowSeries = payload.series
    }
  },
  setStandardLoadedSlug: (state, slug) => {
    state.standardLoadedSlug = slug
  },
  setAdminLoadedSlug: (state, slug) => {
    state.adminLoadedSlug = slug
  },
  setNewMemberNumber: (state, payload) => {
    state.currentShowSeries.members.forEach(m => {
      if (m.id === payload.member_id) {
        m.membership_number = payload.membership_number
      }
    })
  },
  addSponsor_series: (state, payload) => {
    state.currentShowSeries.sponsors.push(payload)
  },
  updateSeriesMember: (state, member) => {
    state.currentShowSeries.members.forEach((m, i) => {
      if (m.id === member.id) {
        Vue.set(state.currentShowSeries.members, i, member)
      }
    })
  },
  admin_updateSanctioningForm: (state, form) => {
    state.currentShowSeries.sanctioning_forms.forEach((f, i) => {
      if (f.id === form.id) {
        Vue.set(state.currentShowSeries.sanctioning_forms, i, form)
      }
    })
  },
  setPointConfigurations: (state, list) => {
    if (state.currentShowSeries) {
      Vue.set(state.currentShowSeries, 'point_configurations', list || [])
    }
  },
  setStandingsConfigurations: (state, list) => {
    if (state.currentShowSeries) {
      Vue.set(state.currentShowSeries, 'standings_configurations', list || [])
    }
  },
  removeMemberFromCurrentShowSeries: (state, payload) => {
    if (!state.currentShowSeries || !state.currentShowSeries.members) return
    Vue.set(
      state.currentShowSeries,
      'members',
      state.currentShowSeries.members.filter(m => m.id !== payload.id)
    )
  }
}

const actions = {
  // Event Actions
  loadCurrentShowSeries: ({commit, rootState}, payload) => {
    return rootState.apiInstance.get(`/showSeries/${payload.slug}`)
      .then(response => {
        commit('setCurrentShowSeries', {series: response.data.data})
        commit('setStandardLoadedSlug', payload.slug)
        // This payload has no members/animal_nominations/payments/payouts, so whatever
        // admin data was in the store is gone - admin views must re-fetch.
        commit('setAdminLoadedSlug', null)
      })
  },
  loadCurrentShowSeriesAdmin: ({commit, rootState}, payload) => {
    return rootState.apiInstance.get(`/showSeries/${payload.slug}/admin`)
      .then(response => {
        commit('setCurrentShowSeries', {series: response.data.data})
        commit('setAdminLoadedSlug', payload.slug)
        // The admin payload is a superset, so standard views are satisfied too.
        commit('setStandardLoadedSlug', payload.slug)
      })
  },
  addSanctioningForm: ({commit, rootState}, payload) => {
    payload.name = 'series_sanctioning'
    return rootState.apiInstance.post('/data/seriesSanctioningForm', payload)
      .then((resp) => {
        payload.id = resp.data.data.id
        commit('userDataAddData', payload)
      })
  },
  updateSanctioningForm: ({commit, rootState}, payload) => {
    return rootState.apiInstance.put(`/data/seriesSanctioningForm/${payload.id}`, payload)
      .then(() => {
        commit('userData_updateSanctioningForm', payload)
      })
  },
  deleteSanctioningForm: ({commit, rootState}, payload) => {
    return rootState.apiInstance.delete(`/data/seriesSanctioningForm/${payload.id}`)
      .then(() => {
        commit('userData_removeSanctioningForm', payload)
      })
      .catch(() => {
      })
  },
  adminUpdateSanctioningForm: ({commit, rootState}, payload) => {
    return rootState.apiInstance.put(`/data/seriesSanctioningForm/${payload.id}`, payload)
      .then(() => {
        commit('admin_updateSanctioningForm', payload)
      })
  },
  addSeriesMember: ({commit, rootState}, payload) => {
    let member = {...payload.member}
    member.show_series_id = payload.show_series_id
    member.membership_number = 0
    member.membership_status = payload.cost ? 'PENDING' : 'ACTIVE'
    member.date_joined = payload.cost ? null : new Date()
    // FIX THIS BUG...
    return rootState.apiInstance.get(`getNextMemberNumber?show_series_id=${member.show_series_id}&skip=${payload.cost !== 0}`)
      .then(response => {
        member.membership_number = response.data.data
        return rootState.apiInstance.post('/data/showSeriesMembers', member)
          .then(response => {
            member.name = 'show_series_membership'
            member.add_ons = []
            member.animal_nominations = []
            member.id = response.data.data.id
            commit('userDataAddData', member)
          })
          .catch(() => {
          })
      })
  },
  // Admin-side add. Unlike addSeriesMember (exhibitor self-join, PENDING until
  // paid) the admin decides the status up front: ACTIVE takes the next member
  // number and today's join date, PENDING sits at 0 until pushed through.
  adminAddSeriesMember: ({rootState, dispatch}, {member, seriesId, slug}) => {
    const paid = member.membership_status === 'ACTIVE'
    const row = {
      show_series_id: seriesId,
      exhibitor_id: member.exhibitor_id,
      user_id: member.user_id,
      year: member.year,
      membership_type: member.membership_type,
      membership_details: member.membership_details,
      membership_status: paid ? 'ACTIVE' : 'PENDING',
      membership_number: 0,
      date_joined: paid ? new Date() : null
    }
    if (member.species) row.species = member.species
    const nextNumber = paid
      ? rootState.apiInstance.get(`getNextMemberNumber?show_series_id=${seriesId}&skip=false`).then(r => r.data.data)
      : Promise.resolve(0)
    return nextNumber
      .then(number => {
        row.membership_number = number
        return rootState.apiInstance.post('/data/showSeriesMembers', row)
      })
      .then(() => dispatch('loadCurrentShowSeriesAdmin', {slug}))
  },
  updateSeriesMember: ({commit, rootState}, member) => {
    return rootState.apiInstance.put(`/data/showSeriesMembers/${member.id}`, member)
      .then(() => {
        commit('updateSeriesMember', member)
      })
  },
  updateMemberAliases: ({commit, state, rootState}, {memberId, aliases}) => {
    return rootState.apiInstance.patch(`/showSeries/members/${memberId}/aliases`, {aliases})
      .then(response => {
        const updated = response.data.data
        const existing = state.currentShowSeries.members.find(m => m.id === memberId)
        if (existing) {
          const next = Object.assign({}, existing, {aliases: updated.aliases})
          commit('updateSeriesMember', next)
        }
        return updated
      })
  },
  deleteSeriesMember: ({commit, rootState}, payload) => {
    return rootState.apiInstance.delete(`/data/showSeriesMembers/${payload.id}`)
      .then(response => {
        commit('userData_removeMember', payload)
        commit('removeMemberFromCurrentShowSeries', payload)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  },
  addMembershipAddOn: ({commit, rootState}, payload) => {
    let addOn = JSON.parse(JSON.stringify(payload))
    return rootState.apiInstance.post('/data/membershipAddOn', addOn)
      .then(response => {
        // payload.name = 'show_series_addon'
        addOn.show_series_member_add_on_id = response.data.data.show_series_member_add_on_id
        commit('userDataAddData_addon', addOn)
      })
      .catch(() => {
      })
  },
  deleteMembershipAddOn: ({commit, rootState}, payload) => {
    return rootState.apiInstance.delete(`/data/membershipAddOn/${payload.show_series_member_add_on_id}`)
      .then(response => {
        commit('userData_removeAddon', payload)
      })
      .catch(() => {
      })
  },
  addAnimalNomination: ({commit, rootState, dispatch}, payload) => {
    let nomination = JSON.parse(JSON.stringify(payload))
    nomination.created_by = '1234'
    return rootState.apiInstance.post('/data/showSeriesAnimalNomination', nomination)
      .then(response => {
        // payload.name = 'show_series_addon'
        nomination.show_series_animal_nomination_id = response.data.data.show_series_animal_nomination_id
        commit('userDataAddData_animalNomination', nomination)
        dispatch('saveAnimal', {exhibitor_id: nomination.exhibitor_id, animal: nomination.animal, entry_ids: [], nomination_id: response.data.data.show_series_animal_nomination_id})
        return response.data.data
      })
      .catch(() => {
      })
  },
  editAnimalNomination: ({commit, rootState, dispatch}, payload) => {
    let nomination = JSON.parse(JSON.stringify(payload))
    return rootState.apiInstance.put(`/data/showSeriesAnimalNomination/${nomination.show_series_animal_nomination_id}`, nomination)
      .then(response => {
        commit('userDataUpdateData_animalNomination', nomination)
        // Resolves with the saved animal, so callers see a newly created animal_id
        return dispatch('saveAnimal', {exhibitor_id: nomination.exhibitor_id, animal: nomination.animal, entry_ids: [], nomination_id: nomination.show_series_animal_nomination_id})
      })
  },
  deleteAnimalNomination: ({commit, rootState}, payload) => {
    return rootState.apiInstance.delete(`/data/showSeriesAnimalNomination/${payload.show_series_animal_nomination_id}`)
      .then(response => {
        if (!payload.isAdmin) {
          commit('userData_removeAnimalNomination', payload)
        }
      })
      .catch(() => {
      })
  },
  adminPushThroughMembership: ({commit, rootState}, payload) => {
    return rootState.apiInstance.post('/updateMemberPaid', payload)
      .then(response => {
        commit('setNewMemberNumber', {
          member_id: payload.member_id,
          membership_number: response.data.data.number
        })
      })
  },
  adminPushThroughAnimalNomination: ({commit, rootState, dispatch}, payload) => {
    return rootState.apiInstance.put(`/data/showSeriesAnimalNomination/${payload.show_series_animal_nomination_id}`, {status: 'PAID'})
      .then(response => {
        if (payload.member.membership_number === 0) {
          dispatch('adminPushThroughMembership', payload.member)
        }
      })
  },
  adminPushThroughAddOn: ({commit, rootState, dispatch}, payload) => {
    return rootState.apiInstance.put(`/data/membershipAddOn/${payload.show_series_member_add_on_id}`, {status: 'PAID'})
      .then(response => {
        if (payload.member.membership_number === 0) {
          dispatch('adminPushThroughMembership', payload.member)
        }
      })
  },
  saveSponsor_series: ({rootState, state, commit, getters}, sponsor) => {
    if (sponsor.sponsor_id) {
      sponsor.last_updated_by = getters.getUserID
      return rootState.apiInstance.put(`/data/sponsors/${sponsor.sponsor_id}`, sponsor)
    } else {
      sponsor.created_by = getters.getUserID
      return rootState.apiInstance.post('/data/sponsors', sponsor)
        .then(result => {
          sponsor.sponsor_id = result.data.data.sponsor_id
          commit('addSponsor_series', sponsor)
        })
    }
  },
  createPointConfiguration: ({rootState}, {showSeriesId, configuration}) => {
    return rootState.apiInstance.post('/data/showSeriesPointConfigurations', {
      show_series_id: showSeriesId,
      configuration
    })
  },
  updatePointConfiguration: ({rootState}, {id, configuration}) => {
    return rootState.apiInstance.put(`/data/showSeriesPointConfigurations/${id}`, {
      configuration
    })
  },
  deletePointConfiguration: ({rootState}, id) => {
    return rootState.apiInstance.delete(`/data/showSeriesPointConfigurations/${id}`)
  },
  createStandingsConfiguration: ({rootState}, {showSeriesId, configuration}) => {
    return rootState.apiInstance.post('/data/showSeriesStandingsConfigurations', {
      show_series_id: showSeriesId,
      configuration
    })
  },
  updateStandingsConfiguration: ({rootState}, {id, configuration}) => {
    return rootState.apiInstance.put(`/data/showSeriesStandingsConfigurations/${id}`, {
      configuration
    })
  },
  deleteStandingsConfiguration: ({rootState}, id) => {
    return rootState.apiInstance.delete(`/data/showSeriesStandingsConfigurations/${id}`)
  }
}

const getters = {
  mySeriesMembers: (state, getters, rootState) => {
    if (state.currentShowSeries && rootState.user) {
      return rootState.userData.show_series_membership.filter(member => member.show_series_id === state.currentShowSeries.show_series_id)
        .map(member => {
          member.exhibitor = rootState.userData.exhibitors.filter(e => e.exhibitor_id === member.exhibitor_id)[0]
          return member
        })
        .filter(member => member.exhibitor)
    } else {
      return []
    }
  },
  myShowSeriesSanctioning: (state, getters, rootState) => {
    if (state.currentShowSeries && rootState.user) {
      return rootState.userData.series_sanctioning.filter(sanctioning => sanctioning.show_series_id === state.currentShowSeries.show_series_id)
    } else {
      return []
    }
  }
}

export default {
  namespaced: false,
  state,
  actions,
  mutations,
  getters
}
